import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const EMAIL_STORAGE_KEY = 'finmodel_user_email';
const EMAIL_NAME_KEY = 'finmodel_user_name';

interface EmailGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string, name?: string) => void;
  action: 'generate' | 'download';
  modelType: string;
  isLoading?: boolean;
}

/**
 * EmailGateModal - Mandatory email capture before model generation or download
 * Stores email in localStorage for returning users
 * Sends to backend Lead capture endpoint
 */
export function EmailGateModal({
  isOpen,
  onClose,
  onSubmit,
  action,
  modelType,
  isLoading = false,
}: EmailGateModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [hasStoredEmail, setHasStoredEmail] = useState(false);

  // Check for stored email on mount
  useEffect(() => {
    const storedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
    const storedName = localStorage.getItem(EMAIL_NAME_KEY);
    if (storedEmail) {
      setEmail(storedEmail);
      setHasStoredEmail(true);
    }
    if (storedName) {
      setName(storedName);
    }
  }, [isOpen]);

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Email is required to continue');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Store in localStorage for returning users
    localStorage.setItem(EMAIL_STORAGE_KEY, email);
    if (name.trim()) {
      localStorage.setItem(EMAIL_NAME_KEY, name);
    }

    onSubmit(email, name || undefined);
  };

  const actionText = action === 'generate' ? 'Generate Model' : 'Download Excel';
  const actionDescription =
    action === 'generate'
      ? 'Enter your email to generate your personalized financial model'
      : 'Enter your email to download your Excel financial model';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.4 }}
          >
            {/* Header with gradient */}
            <div className="bg-gradient-to-br from-primary-600 to-primary-700 px-6 py-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">
                  {action === 'generate' ? '📊' : '📥'}
                </span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {hasStoredEmail ? 'Welcome Back!' : 'Almost There!'}
              </h2>
              <p className="text-primary-100 text-sm">{actionDescription}</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="founder@startup.com"
                  className={`
                    w-full px-4 py-3 rounded-lg border-2 transition-all
                    text-slate-900 placeholder:text-slate-400
                    focus:outline-none focus:ring-0
                    ${error ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-primary-500'}
                  `}
                  autoComplete="email"
                  autoFocus={!hasStoredEmail}
                />
                {error && (
                  <p className="mt-1.5 text-sm text-red-600">{error}</p>
                )}
              </div>

              {/* Name Field (Optional but encouraged) */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Your Name{' '}
                  <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 transition-all text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 focus:border-primary-500"
                  autoComplete="name"
                />
              </div>

              {/* Model Type Info */}
              <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-lg">
                  📈
                </div>
                <div className="text-sm">
                  <p className="font-medium text-slate-700 capitalize">
                    {modelType.replace(/([a-z])([A-Z])/g, '$1 $2')} Model
                  </p>
                  <p className="text-slate-500">
                    5-year financial projections
                  </p>
                </div>
              </div>

              {/* Benefits */}
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>100% Free - No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Unlimited model regenerations</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-green-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>No spam - Only product updates</span>
                </div>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3.5 rounded-lg transition-all shadow-lg shadow-primary-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    {action === 'generate' ? '🚀' : '📥'} {actionText}
                  </>
                )}
              </motion.button>

              {/* Privacy Note */}
              <p className="text-xs text-center text-slate-400">
                By continuing, you agree to our{' '}
                <a
                  href="/privacy"
                  className="text-primary-600 hover:underline"
                  target="_blank"
                >
                  Privacy Policy
                </a>
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Check if user has already provided email
 */
export function hasStoredEmail(): boolean {
  return !!localStorage.getItem(EMAIL_STORAGE_KEY);
}

/**
 * Get stored email
 */
export function getStoredEmail(): string | null {
  return localStorage.getItem(EMAIL_STORAGE_KEY);
}

/**
 * Get stored name
 */
export function getStoredName(): string | null {
  return localStorage.getItem(EMAIL_NAME_KEY);
}
